import { getRedirectUrlOnSignInComplete } from '@/components/Okta/utils/util'
import analytics from '@/shared-utils/analytics'
import {
  type FlowIdentifier,
  IdxStatus,
  type IdxTransaction,
  isRedirectUri,
  OktaAuth,
  type Tokens,
} from '@okta/okta-auth-js'
import { Security } from '@okta/okta-react'
import { useRouter } from 'next/router'
import { Box, ButtonChip, Container, Flex, Link, Spinner, Text } from 'pcln-design-system'
import { type ReactNode, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import BackButton from './BackButton'
import CanceledPage from './CanceledPage'
import ErrorPage from './ErrorPage'
import FlowPage from './FlowPage'
import { fireLoginSuccessEvent, firePageViewEvent } from './ga4'
import LoginCallback from './LoginCallback'
import TerminalPage from './TerminalPage'
import type { AuthTabType, InitialTransaction } from './utils/types'

const WebviewContainer = styled(Flex)`
  width: 100%;
  height: 100%;
  background-color: white;
  position: fixed;
  z-index: 4;
  top: 0px;
`
const WebviewLoader = styled(Spinner)`
  top: 40%;
`

const oktaAuth = ({
  clientId,
  issuer,
  callbackUrl,
  clientScope,
}: {
  clientScope: string
  clientId: string
  issuer: string
  callbackUrl: string
}) => {
  return new OktaAuth({
    clientId,
    issuer,
    redirectUri: callbackUrl,
    scopes: ['openid', 'profile', 'email', 'offline_access', clientScope],
  })
}

const restoreOriginalUri = () => {
  window.location.href = '/'
}

const AUTH_TABS = [
  {
    autobotID: 'AUTH_TAB_SIGN_IN',
    name: 'SIGN IN',
    tabText: 'Sign In',
    id: 'authenticate',
    label: 'Sign In to your account',
    query: { flow: 'authenticate' },
  },
  {
    autobotID: 'AUTH_TAB_CREATE_ACCOUNT',
    name: 'CREATE ACCOUNT',
    tabText: 'Create Account',
    id: 'register',
    label: 'Create an account',
    query: { flow: 'register' },
  },
] as const

function logSignInSuccess(redirectUrl: string, activeFlow: FlowIdentifier) {
  analytics.log({
    message: `Standalone - login complete event received`,
  })
  const signInMethod = sessionStorage.getItem('standalone_signin_with')
  if (signInMethod) {
    sessionStorage.removeItem('standalone_signin_with')
    fireLoginSuccessEvent(signInMethod, redirectUrl, activeFlow)
  }
}

function Okta({
  redirectUrl,
  initialTransaction = null,
  clientId,
  issuer,
  callbackUrl,
  clientScope,
}: InitialTransaction) {
  const [transaction, setTransaction] = useState<IdxTransaction | null>(initialTransaction)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [showSpinner, setShowSpinner] = useState(false)
  const [isWebview, setIsWebview] = useState(false)
  const [slideInRight, setSlideInRight] = useState(true)
  const [componentToShow, setComponentToShow] = useState<ReactNode>()
  const [buttonsDisabled, setButtonsDisabled] = useState(false)

  const oktaClient = useMemo(
    () => oktaAuth({ clientScope, clientId, issuer, callbackUrl }),
    [clientId, clientScope, issuer, callbackUrl],
  )

  const { asPath, push, query, pathname, replace } = useRouter()

  const logAnalytics = (message: string) => {
    analytics.log({
      message,
    })
  }

  const [activeFlow, setActiveFlow] = useState(
    query.flow ? (query.flow as FlowIdentifier) : ('register' as FlowIdentifier),
  )
  useEffect(() => {
    const { tokens, status } = transaction || {
      tokens: {},
      status: '',
    }

    const proceedOktaFlow = async () => {
      if (!localStorage.getItem('standalone_redirect')) {
        localStorage.setItem('standalone_redirect', redirectUrl)
      }
      try {
        await oktaClient.revokeRefreshToken()
        oktaClient.tokenManager.clear()
        const newTransaction = await oktaClient.idx.proceed()
        setTransaction(newTransaction)
      } catch (error) {
        const catchErrorMessage =
          error instanceof Error ? error.message : (error?.toString() ?? 'Unknown error')
        analytics.logError({
          message: 'Standalone - proceedOktaFlow failed',
          errorMessage: catchErrorMessage,
          redirectUrl,
        })
      }
    }

    const startOkta = async () => {
      if (status === IdxStatus.SUCCESS && tokens === undefined) {
        await proceedOktaFlow()
      } else if (status === IdxStatus.SUCCESS) {
        logSignInSuccess(redirectUrl, activeFlow)

        oktaClient.tokenManager.setTokens(tokens as Tokens)
        sessionStorage.removeItem('checkoutURL')
      } else if (status === IdxStatus.TERMINAL) {
        logAnalytics(`Standalone - switched to Terminal page`)
        sessionStorage.setItem('okta_terminal_message', JSON.stringify(transaction?.messages, null, 4))
        if (isWebview) {
          analytics.log({
            message: `webview: sign in failed. Terminal page`,
          })
          window.location.href = getRedirectUrlOnSignInComplete('/', document.baseURI)
        } else {
          void push('/home/join/terminal')
        }
      } else if (status === IdxStatus.FAILURE) {
        logAnalytics(`Standalone - switched to Error page`)
        if (isWebview) {
          analytics.log({
            message: `webview: sign in failed. Error page`,
          })
          window.location.href = getRedirectUrlOnSignInComplete('/', document.baseURI)
        } else {
          void push('/home/join/error')
        }
      } else if (status === IdxStatus.CANCELED) {
        logAnalytics(`Standalone - switched to Canceled page`)
        void push('/home/join/canceled')
      }
    }

    void startOkta()
  }, [transaction, oktaClient, push, redirectUrl, activeFlow, isWebview])

  useEffect(() => {
    firePageViewEvent(activeFlow)
  }, [activeFlow])

  useEffect(() => {
    const resumeTransaction = async () => {
      try {
        const newTransaction: IdxTransaction = await oktaClient.idx.proceed()
        setTransaction(newTransaction)
      } catch (error) {
        const catchErrorMessage =
          error instanceof Error ? error.message : (error?.toString() ?? 'Unknown error')
        analytics.logError({
          message: `Standalone - resume transaction failed: ${catchErrorMessage}`,
        })
      }
    }

    const checkIsWebview =
      sessionStorage.getItem('isWebview') === 'true' ||
      !!new URLSearchParams(window.location.search).get('webview')

    setIsWebview(checkIsWebview)

    if (
      !asPath?.includes('error') &&
      !isRedirectUri(window.location.href, oktaClient) &&
      oktaClient.idx.canProceed()
    ) {
      void resumeTransaction()
    }
  }, [asPath, oktaClient])

  useEffect(() => {
    let component = (
      <FlowPage
        redirectUrl={redirectUrl}
        transaction={transaction}
        setTransaction={setTransaction}
        setSlideInRight={setSlideInRight}
        slideInRight={slideInRight}
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
        showSpinner={showSpinner}
        setShowSpinner={setShowSpinner}
        flowParameter={activeFlow}
        buttonsDisabled={buttonsDisabled}
        isWebview={isWebview}
        setButtonsDisabled={setButtonsDisabled}
      />
    )
    if (asPath?.includes('terminal')) {
      component = <TerminalPage />
    }

    if (asPath?.includes('error')) {
      component = <ErrorPage />
    }

    if (asPath?.includes('canceled')) {
      component = <CanceledPage />
    }

    if (asPath?.includes('login/callback')) {
      component = <LoginCallback transaction={transaction} setTransaction={setTransaction} />
    }

    setComponentToShow(component)
  }, [
    redirectUrl,
    transaction,
    errorMessage,
    showSpinner,
    activeFlow,
    buttonsDisabled,
    asPath,
    slideInRight,
    oktaClient,
    isWebview,
  ])

  const handleOnClick = (tab: AuthTabType) => {
    setErrorMessage('')
    if (activeFlow !== tab.id) {
      setShowSpinner(true)
    }

    setActiveFlow(tab.id)
    void push(
      {
        pathname,
        query: {
          ...query,
          flow: tab.query.flow,
        },
      },
      undefined,
      { shallow: true },
    )
  }
  const isBackButtonHidden = transaction?.nextStep?.name === 'enroll-authenticator'

  return (
    <Security oktaAuth={oktaClient} restoreOriginalUri={restoreOriginalUri}>
      {isWebview && (
        <WebviewContainer justifyContent='space-around'>
          <WebviewLoader data-testid='full-spinner' />
        </WebviewContainer>
      )}
      {buttonsDisabled && <div className='bg-white absolute z-[4] h-full w-full opacity-50' />}
      <Container size='sm' p={4}>
        <Flex flexDirection='column' justifyContent='space-between' style={{ minHeight: '90vh' }}>
          <Box px={[0, null, 5]}>
            {!isBackButtonHidden && <BackButton onNavigateBack={replace} redirectUrl={redirectUrl} />}
            <Flex alignItems='center' justifyContent='center' mt={[3, null, 4]} mb={24} role='tablist'>
              {AUTH_TABS.map((tab) => {
                const isActive = activeFlow !== undefined ? tab.id === activeFlow : tab.id === 'register'

                return (
                  <Box width={1 / 2} mx={1} key={tab.id}>
                    <ButtonChip
                      disabled={!transaction?.nextStep || showSpinner || buttonsDisabled}
                      id={`tab-${tab.id}`}
                      data-testid={tab.autobotID}
                      aria-selected={isActive}
                      aria-label={tab.label}
                      size='md'
                      role='tab'
                      width='100%'
                      expanded={isActive}
                      onClick={() => {
                        void handleOnClick(tab)
                      }}
                    >
                      <Box px={3} py={1}>
                        <Text textAlign='center' regular fontSize={1}>
                          {tab.tabText}
                        </Text>
                      </Box>
                    </ButtonChip>
                  </Box>
                )
              })}
            </Flex>
            {componentToShow}
            <Box mb='48px' />
          </Box>
          <Box px={[0, null, 5]}>
            <Text textStyle='disclaimer'>
              By signing in or creating an account, you agree with our{' '}
              <Link href='/static-pages/terms_en.html'>Terms & Conditions </Link> and{' '}
              <Link href='/static-pages/privacy-policy.html'>Privacy Statement</Link>.
            </Text>
          </Box>
        </Flex>
      </Container>
    </Security>
  )
}

export default Okta
